/**
 * @license
 * Copyright© Celligence International, LLC - 2023
 * This software program is protected by copyright laws and is the property of
 * Celligence International, LLC (Celligence). Any unauthorized reproduction,
 * distribution, modification, or public display of this program, without the
 * explicit permission of Celligence is strictly prohibited. If you have gained
 * access to this program without the expressed approval of Celligence report
 * the incident to legal@celligence.com.
*/
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import MultiCarousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Classes from '../whySunwest/index.module.css';

const ReviewCarousel = () => {
  const [reviews, setReviews] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [infiniteStart, setInfiniteStart] = useState(1);

  const fetchReviews = async (page) => {
    setIsLoading(true);
    try {
      const response = await axios.post('https://exj3cjwpp3.execute-api.us-west-1.amazonaws.com/prod/Swmc-Social-Survey', {
        pageNumber: page
      });

      if (response.status === 200) {
        const newTestimonials = response.data;
        if (Array.isArray(newTestimonials)) {
          const transformedReviews = newTestimonials.map((ele) => {
            const date = new Date(ele.survey_completed_on);
            date.setDate(date.getDate() - 1); // Subtract one day from the date
            const monthNames = [
              "January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December"
            ];
            const formattedDate = `on ${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
            const lastName = `${ele.customer_last_name.charAt(0)}.`;
            const firstName = ele.customer_first_name;

            return {
              id: ele.id,
              text: ele.review,
              rating: ele.rating,
              date: formattedDate,
              author: `${lastName}, ${formattedDate}`,
              name: ` ${firstName} ${lastName}`,
              showFullReview: false // Add a property to track if full review is visible or not
            };
          });

          setReviews([...reviews, ...transformedReviews]);

          if (transformedReviews.length >= 8) {
            setPageNumber(pageNumber + 1);
          }
        }
      } else {
        throw new Error('Request failed');
      }
    } catch (err) {
      
    }
    setIsLoading(false);
  };

  const getNewTestimonials = (page) => {
    fetchReviews(page);
  };

  useEffect(() => {
    getNewTestimonials(infiniteStart);
  }, [infiniteStart]);

  const handleSlideChange = () => {
    setTimeout(() => {
      if (infiniteStart < 500) {
        const nextPage = pageNumber + 1;
        setPageNumber(nextPage);
        setInfiniteStart(infiniteStart + 1);
        getNewTestimonials(nextPage);
      }
    }, 500);
  };

  const toggleFullReview = (reviewId) => {
    setReviews((prevReviews) =>
      prevReviews.map((review) => {
        if (review.id === reviewId) {
          return {
            ...review,
            showFullReview: !review.showFullReview
          };
        }
        return review;
      })
    );
  };

  const responsiveConfig = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 4,
      slidesToSlide: 4
    },
    tablet: {
      breakpoint: { max: 1024, min: 630 },
      items: 2,
      slidesToSlide: 2
    },
    mobile: {
      breakpoint: { max: 630, min: 0 },
      items: 1,
      slidesToSlide: 1
    }
  };

  return (
    <div>
      <img
        src={"https://stagingresources.swmc.com/swmc-images/Latest Testimonialsdesk.webp"}
        className={Classes.latestheading}
        loading='lazy'
      />
      <div className={Classes.carouselContainer}>
        <MultiCarousel
          responsive={responsiveConfig}
          swipeable
          draggable
          arrows={false}
          customTransition="transform 500ms ease-in-out"
          afterChange={handleSlideChange}
        >
          {reviews.map((review, index) => (
            <div key={review.id}>
              <Container>
                <Card
                  className={`${Classes.card_Style} ${
                    index % 2 === 0 ? Classes.orangeText : Classes.blueText
                  } `}
                  id={Classes.review_item}
                >
                  <Row>
                    <Col md={12}>
                      <div className={Classes.horizontal_author_details}>
                        <Row className={Classes.testimonials_rating}>
                          <Col>
                            <fieldset className={Classes.ratingcolor} disabled>
                              {[...Array(5)].map((_, i) => (
                                <span
                                  key={i}
                                  className={`fa fa-star ${i < review.rating ? 'filled' : ''}`}
                                ></span>
                              ))}
                            </fieldset>
                          </Col>
                          <Col>
                            <p className={Classes.datetext}>{review.date}</p>
                          </Col>
                        </Row>
                      </div>
                      <Row>
                        <p className={Classes.description}>
                          {review.showFullReview || review.text.length <= 150
                            ? review.text
                            : `${review.text.slice(0, 100)}...`}{' '}
                          {review.text.length > 100 && (
                            <span
                              className={Classes.seeMoreLink}
                              onClick={() => toggleFullReview(review.id)}
                            >
                              {review.showFullReview ? 'See Less' : 'See More'}
                            </span>
                          )}
                        </p>
                      </Row>
                      <Row>
                        <h6 className={Classes.name}>{review.name}</h6>
                      </Row>
                    </Col>
                  </Row>
                </Card>
              </Container>
            </div>
          ))}
        </MultiCarousel>
        {isLoading && (
          <p>Loading...</p>
        )}
      </div>
    </div>
  );
};

export default ReviewCarousel;
